// Pager footer for the document frame (.doc-frame__pager). One control
// used by both modes — the read-only content-slice view and the CKEditor
// edit view — so page navigation is identical everywhere (Rule 3 of the
// Document Frame design). Lives in components/ so the prev/next triggers
// can be raw <button>s (the no-raw-<button> lint rule is screens-only).
import type { ReactNode } from "react";

export interface DocPagerProps {
  /** 1-based current page. */
  page: number;
  /** Total page count (>= 1). */
  total: number;
  onPrev: () => void;
  onNext: () => void;
  /** Left-aligned status text (e.g. "read-only · v2.1.0" or "draft · unsaved"). */
  meta?: ReactNode;
  ariaLabel?: string;
  /** Base test id; `${testId}-prev|-next|-label` are derived from it. */
  testId?: string;
}

export function DocPager({
  page,
  total,
  onPrev,
  onNext,
  meta,
  ariaLabel = "Document pages",
  testId,
}: DocPagerProps): JSX.Element {
  return (
    <nav className="doc-frame__pager" aria-label={ariaLabel} data-testid={testId}>
      <span className="doc-frame__pager__meta">{meta}</span>
      <div className="doc-frame__pager__ctrls">
        <button
          type="button"
          className="doc-frame__pager__btn"
          onClick={onPrev}
          disabled={page <= 1}
          aria-label="Previous page"
          data-testid={testId !== undefined ? `${testId}-prev` : undefined}
        >
          ‹ Prev
        </button>
        <span
          className="doc-frame__pager__lbl"
          data-testid={testId !== undefined ? `${testId}-label` : undefined}
        >
          Page {page} of {total}
        </span>
        <button
          type="button"
          className="doc-frame__pager__btn"
          onClick={onNext}
          disabled={page >= total}
          aria-label="Next page"
          data-testid={testId !== undefined ? `${testId}-next` : undefined}
        >
          Next ›
        </button>
      </div>
    </nav>
  );
}
